import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Maximize2, Mic, Minimize2, Volume2, VolumeX } from 'lucide-react';
import { useAskAiVoice } from '../../hooks/useAskAiVoice';
import { unlockBrowserSpeech } from '../../utils/browserSpeech';
import AskAiRichText from '../../components/AskAiRichText';
import AskAiVideoGallery, { AskAiVideo, stripYoutubeUrlsFromAnswer } from '../../components/AskAiVideoGallery';
import { AskAiMobileScrim, useAskAiMobileFullscreen } from '../../components/AskAiMobileFullscreen';
import { apiService } from '../../services/api';
import { AskAiSignupDraft, AskAiSignupStatus, AskAiTurn } from '../../types';
import { askAiRequestErrorMessage } from '../../utils/askAiErrors';
import { newAskAiSessionId } from '../../utils/askAiSession';
import { loadPlatformAskAiSession, savePlatformAskAiSession } from '../../utils/platformAskAiSession';
import { formatPhoneNumber } from '../../utils/phoneFormatUtils';
import {
  nameLooksFabricated,
  phoneLooksFabricated,
  REGISTRATION_IDENTITY_MESSAGES,
} from '../../utils/registrationIdentityErrors';
import { Icon } from './FeaturesIcons';
import '../../components/PlatformAskAi.css';

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  role: ChatRole;
  text: string;
  link?: [string, string];
  suggest?: string[];
  videos?: AskAiVideo[];
  signup?: AskAiSignupStatus;
  pending?: boolean;
  failed?: boolean;
};

function assistantDisplayText(message: ChatMessage): string {
  return message.videos?.length ? stripYoutubeUrlsFromAnswer(message.text) : message.text;
}

type SignupSecrets = { password?: string; confirmPassword?: string };

type ChatThread = {
  id: number;
  sessionId: string;
  created: number;
  messages: ChatMessage[];
  signup?: AskAiSignupDraft;
  secrets?: SignupSecrets;
};

const TOPICS: Array<[string, string]> = [
  ['Help me create a customer account', 'spark'],
  ['How does AI booking work?', 'spark'],
  ['What comes with my website?', 'web'],
  ['How do quotes and payments work?', 'card'],
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function publicSignupDraft(status?: AskAiSignupStatus, previous?: AskAiSignupDraft): AskAiSignupDraft {
  if (status?.created || status?.alreadyLoggedIn) return {};
  if (!status) {
    return previous?.wanted ? { ...previous, wanted: true } : {};
  }
  if (!status.wanted && !status.ready) return {};
  return {
    wanted: true,
    firstName: status.firstName,
    lastName: status.lastName,
    email: status.email,
    phone: status.phone,
    birthMonth: status.birthMonth,
    birthDay: status.birthDay,
    skipBirthday: status.skipBirthday,
    acceptedAgreement: status.acceptedAgreement,
  };
}

function newThread(id = 1): ChatThread {
  return { id, sessionId: newAskAiSessionId(), created: Date.now(), messages: [] };
}

function reviveSecrets(value: unknown): SignupSecrets | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const secrets = value as SignupSecrets;
  const password = typeof secrets.password === 'string' ? secrets.password : undefined;
  const confirmPassword = typeof secrets.confirmPassword === 'string' ? secrets.confirmPassword : undefined;
  if (!password && !confirmPassword) return undefined;
  return { password, confirmPassword };
}

function reviveMessages(value: unknown): ChatMessage[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const message = item as ChatMessage;
    if ((message.role !== 'user' && message.role !== 'assistant') || typeof message.text !== 'string') return [];
    if (message.pending) return [];
    return [{
      role: message.role,
      text: message.text,
      link: Array.isArray(message.link) && message.link.length === 2 ? message.link : undefined,
      suggest: Array.isArray(message.suggest) ? message.suggest.filter((chip) => typeof chip === 'string') : undefined,
      videos: Array.isArray(message.videos) ? message.videos : undefined,
      signup: message.signup,
      failed: Boolean(message.failed) || undefined,
    }];
  });
}

function reviveChats(value: unknown): ChatThread[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const chats = value.flatMap((item, index) => {
    if (!item || typeof item !== 'object') return [];
    const thread = item as ChatThread;
    const id = Number(thread.id) || index + 1;
    return [{
      id,
      sessionId: typeof thread.sessionId === 'string' && thread.sessionId ? thread.sessionId : newAskAiSessionId(),
      created: Number(thread.created) || Date.now(),
      messages: reviveMessages(thread.messages),
      signup: thread.signup && typeof thread.signup === 'object' ? thread.signup : undefined,
      secrets: reviveSecrets(thread.secrets),
    }];
  });
  return chats.length ? chats : null;
}

function visibleSuggestions(message: ChatMessage): string[] {
  const suggestions = message.suggest || [];
  if (message.signup?.step === 'agreement') {
    return suggestions.filter((suggestion) => suggestion !== 'I agree');
  }
  return suggestions;
}

function hydratePlatformAskAi(): { serial: number; chats: ChatThread[]; active: number } {
  const saved = loadPlatformAskAiSession();
  const chats = reviveChats(saved?.chats);
  if (!saved || !chats) {
    return { serial: 1, chats: [newThread()], active: 1 };
  }
  const active = chats.some((chat) => chat.id === saved.active) ? saved.active : chats[chats.length - 1].id;
  return { serial: Math.max(saved.serial, ...chats.map((chat) => chat.id)), chats, active };
}

type PlatformAskAiProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const PlatformAskAi: React.FC<PlatformAskAiProps> = ({ open, onOpenChange }) => {
  const [hydrated] = useState(hydratePlatformAskAi);
  const [serial, setSerial] = useState(hydrated.serial);
  const [chats, setChats] = useState<ChatThread[]>(hydrated.chats);
  const [active, setActive] = useState(hydrated.active);
  const [draft, setDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const historyTitleRef = useRef<HTMLHeadingElement>(null);
  const activeRef = useRef(active);
  const chatsRef = useRef(chats);
  activeRef.current = active;
  chatsRef.current = chats;

  const current = useMemo(
    () => chats.find((chat) => chat.id === active) ?? chats[0],
    [active, chats]
  );
  const { mobileFullscreen, mobileCompact, toggleFullscreen } = useAskAiMobileFullscreen(open);
  const latestSignup = [...(current.messages || [])].reverse().find((message) => message.role === 'assistant' && !message.pending)?.signup;
  const latestSignupRef = useRef(latestSignup);
  const sendRef = useRef<(raw: string, extras?: AskAiSignupDraft) => Promise<void>>(async () => undefined);
  const listenBaseRef = useRef('');
  latestSignupRef.current = latestSignup;

  const voice = useAskAiVoice({
    active: open && !showHistory,
    onInterimTranscript: (text) => {
      setDraft([listenBaseRef.current, text].filter(Boolean).join(' ').trim());
    },
    onFinalTranscript: (text) => {
      const next = [listenBaseRef.current, text].filter(Boolean).join(' ').trim();
      listenBaseRef.current = '';
      if (next) {
        void sendRef.current(next, latestSignupRef.current?.wanted ? { wanted: true } : undefined);
      }
    },
  });
  const { setSpeechError, stopListening, stopSpeaking, speak } = voice;
  const speakRef = useRef(speak);
  speakRef.current = speak;

  const resize = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = '36px';
    input.style.height = `${Math.min(Math.max(input.scrollHeight, 36), 110)}px`;
  }, []);

  useEffect(() => {
    resize();
  }, [draft, resize]);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [current.messages, showHistory, open, pending]);

  useEffect(() => {
    if (open) {
      setShowHistory(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || pending || showHistory || voice.listening) return;
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open, pending, showHistory, voice.listening]);

  useEffect(() => {
    if (showHistory) {
      historyTitleRef.current?.focus();
    }
  }, [showHistory]);

  useEffect(() => {
    savePlatformAskAiSession({ serial, chats, active, open });
  }, [serial, chats, active, open]);

  useEffect(() => {
    let cancelled = false;
    apiService.getPlatformAskAiStatus()
      .then((status) => {
        if (!cancelled) setEnabled(Boolean(status.enabled));
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateActiveChat = (updater: (chat: ChatThread) => ChatThread) => {
    const chatId = activeRef.current;
    setChats((prev) => prev.map((chat) => (chat.id === chatId ? updater(chat) : chat)));
  };

  const send = useCallback(async (raw: string, extras?: AskAiSignupDraft) => {
    const text = String(raw).trim().slice(0, 800);
    if (!text || pending) return;
    stopListening();
    stopSpeaking();
    setSpeechError('');
    unlockBrowserSpeech();
    setShowHistory(false);
    setDraft('');

    const thread = chatsRef.current.find((chat) => chat.id === activeRef.current);
    const prior: AskAiTurn[] = (thread?.messages || [])
      .filter((message) => !message.pending && !message.failed)
      .map((message) => ({ role: message.role, content: message.text }));
    const nextSecrets: SignupSecrets = {
      password: extras?.password || thread?.secrets?.password,
      confirmPassword: extras?.confirmPassword || thread?.secrets?.confirmPassword,
    };
    const signupPayload: AskAiSignupDraft = {
      ...thread?.signup,
      ...extras,
      wanted: Boolean(thread?.signup?.wanted || extras?.wanted || extras?.confirm || latestSignup?.wanted),
      password: nextSecrets.password,
      confirmPassword: nextSecrets.confirmPassword,
    };

    updateActiveChat((chat) => ({
      ...chat,
      secrets: nextSecrets,
      messages: [
        ...chat.messages.filter((message) => !message.pending && !message.failed),
        { role: 'user', text },
        { role: 'assistant', text: 'Thinking…', pending: true },
      ],
    }));
    setPending(true);

    try {
      const data = await apiService.askPlatform({
        query: text,
        history: prior,
        sessionId: thread?.sessionId || newAskAiSessionId(),
        signup: signupPayload.wanted || signupPayload.confirm ? signupPayload : undefined,
      });
      updateActiveChat((chat) => {
        const priorSignup = [...chat.messages]
          .reverse()
          .find((message) => message.role === 'assistant' && message.signup?.wanted)?.signup;
        return {
          ...chat,
          signup: publicSignupDraft(data.signup, chat.signup),
          secrets: data.signup?.created || data.signup?.alreadyLoggedIn ? {} : nextSecrets,
          messages: [
            ...chat.messages.filter((message) => !message.pending),
            {
              role: 'assistant',
              text: data.answer || 'I can explain AACore Booking features shown on this page.',
              link: data.link,
              suggest: data.suggestions,
              videos: data.videos,
              signup: data.signup || priorSignup,
            },
          ],
        };
      });
      const answer = data.answer || 'I can explain AACore Booking features shown on this page.';
      const spoken = data.videos?.length ? stripYoutubeUrlsFromAnswer(answer) : answer;
      void speakRef.current(spoken);
    } catch (error) {
      updateActiveChat((chat) => ({
        ...chat,
        messages: [
          ...chat.messages.filter((message) => !message.pending),
          {
            role: 'assistant',
            text: enabled
              ? askAiRequestErrorMessage(error)
              : 'AACore AI is not available right now.',
            failed: true,
            suggest: [text],
          },
        ],
      }));
    } finally {
      setPending(false);
    }
  }, [enabled, pending, latestSignup?.wanted, setSpeechError, stopListening, stopSpeaking]);

  sendRef.current = send;

  const sendChip = (chip: string, signup?: AskAiSignupStatus) => {
    if (chip === 'I agree') {
      void send('I agree', { wanted: true, acceptedAgreement: true });
      return;
    }
    if (chip === 'Skip' && signup?.step === 'birthday') {
      void send('Skip', { wanted: true, skipBirthday: true });
      return;
    }
    if (chip === 'Create my account') {
      void send('Create my account', { wanted: true, confirm: true });
      return;
    }
    void send(chip, signup?.wanted ? { wanted: true } : undefined);
  };

  const startNewChat = () => {
    if (pending) return;
    if (current.messages.length) {
      const nextId = serial + 1;
      setSerial(nextId);
      setChats((prev) => [...prev, newThread(nextId)]);
      setActive(nextId);
    }
    setShowHistory(false);
    setDraft('');
    stopSpeaking();
    inputRef.current?.focus();
  };

  const handleLink = (href: string) => {
    if (href.startsWith('#')) {
      onOpenChange(false);
    }
  };

  const latestAssistantIndex = [...current.messages].map((message, index) => ({ message, index }))
    .reverse()
    .find((item) => item.message.role === 'assistant' && !item.message.pending)?.index;

  return (
    <>
      <AskAiMobileScrim show={mobileCompact} onClose={() => onOpenChange(false)} />
      <section
        className={`aac-chat${mobileFullscreen ? ' is-fullscreen' : ''}${mobileCompact ? ' is-compact' : ''}`}
        id="aac-chat"
        aria-label="Ask AACore AI product questions"
        aria-modal={mobileCompact || mobileFullscreen}
        hidden={!open}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            if (showHistory) {
              setShowHistory(false);
              document.getElementById('history-button')?.focus();
            } else {
              onOpenChange(false);
            }
          }
        }}
      >
        <header className="aac-header">
          <div className="aac-topline">
            <h2 className="aac-brand">Ask AACore <small>AI</small></h2>
            <div className="aac-tools">
              <button
                className="aac-icon"
                id="history-button"
                type="button"
                aria-label="Chat history"
                title="Chat history"
                aria-expanded={showHistory}
                aria-controls="chat-history"
                onClick={() => setShowHistory((value) => !value)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11a9 9 0 1 1 2.6 7.3M3 4v7h7M12 7v5l3 2" /></svg>
              </button>
              {voice.canSpeak ? (
                <button
                  className={`aac-icon${voice.speaking ? ' is-speaking' : ''}`}
                  type="button"
                  aria-label={voice.voiceReplies ? 'Mute spoken replies' : 'Unmute spoken replies'}
                  aria-pressed={voice.voiceReplies}
                  title={voice.voiceReplies ? 'Mute spoken replies' : 'Speak replies aloud'}
                  onClick={voice.toggleVoiceReplies}
                >
                  {voice.voiceReplies ? <Volume2 /> : <VolumeX />}
                </button>
              ) : null}
              <button
                className="aac-new"
                type="button"
                title="Start a new conversation. Earlier chats stay in History for this page session."
                onClick={startNewChat}
              >
                New chat
              </button>
              <button
                className="aac-icon aac-fullscreen-toggle"
                type="button"
                aria-label={mobileFullscreen ? 'Exit fullscreen chat' : 'Open chat fullscreen'}
                aria-pressed={mobileFullscreen}
                title={mobileFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                onClick={toggleFullscreen}
              >
                {mobileFullscreen ? <Minimize2 /> : <Maximize2 />}
              </button>
              <button className="aac-icon" type="button" aria-label="Close chat" onClick={() => onOpenChange(false)}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </div>
          </div>
          <p className="aac-subtitle">Questions about AACore Booking?</p>
        </header>
        <div className="aac-body">
          <div
            className="aac-messages"
            id="messages"
            role="log"
            aria-label="Conversation"
            aria-live="polite"
            aria-relevant="additions text"
            aria-hidden={showHistory}
            ref={logRef}
          >
            {!current.messages.length ? (
              <div className="aac-empty">
                <span className="aac-welcome-icon"><Icon name="spark" /></span>
                <h3>What would you like to know?</h3>
                <p>Ask about AACore Booking, or create a customer account right here in chat.</p>
                <div className="aac-actions">
                  {TOPICS.map(([text, icon]) => (
                    <button key={text} className="aac-action" type="button" onClick={() => void send(text)} disabled={pending}>
                      <Icon name={icon} />
                      {text}
                    </button>
                  ))}
                </div>
                <p className="aac-notice">I can explain AACore Booking and help you create a customer account. Passwords stay in private fields, not chat history.</p>
              </div>
            ) : (
              <>
                <div className="aac-date">This conversation</div>
                {current.messages.map((message, index) => (
                  <article key={`${message.role}-${index}`} className={`aac-message ${message.role}`} aria-label={message.role === 'user' ? 'You' : 'AACore AI'}>
                    {message.role === 'user' ? (
                      <div className="aac-bubble">{message.text}</div>
                    ) : (
                      <>
                        <div className="aac-speaker">
                          <Icon name="spark" />
                          AACore AI
                          {index === latestAssistantIndex && !message.pending && !message.failed && voice.canSpeak && voice.voiceReplies ? (
                            <button
                              className={`aac-replay${voice.speaking ? ' is-speaking' : ''}`}
                              type="button"
                              aria-label={voice.speaking ? 'Stop spoken reply' : 'Play spoken reply'}
                              title={voice.speaking ? 'Stop speaking' : 'Play this reply'}
                              onClick={() => {
                                if (voice.speaking) {
                                  voice.stopSpeaking();
                                  return;
                                }
                                void voice.speak(assistantDisplayText(message));
                              }}
                            >
                              {voice.speaking ? <VolumeX /> : <Volume2 />}
                            </button>
                          ) : null}
                        </div>
                        <div className="aac-answer">
                          {message.pending ? (
                            <p className="aac-pending">{message.text}</p>
                          ) : (
                            <>
                              {assistantDisplayText(message) ? (
                                <AskAiRichText text={assistantDisplayText(message)} accentColor="#124e7c" />
                              ) : null}
                              {message.videos && message.videos.length > 0 && (
                                <AskAiVideoGallery videos={message.videos} />
                              )}
                              {index === latestAssistantIndex && message.signup?.wanted && !message.signup.created && (
                                <SignupFields
                                  signup={message.signup}
                                  secrets={current.secrets}
                                  disabled={pending}
                                  onSubmit={(query, extras) => void send(query, extras)}
                                />
                              )}
                            </>
                          )}
                          {message.failed && message.suggest?.[0] && (
                            <button className="aac-retry" type="button" onClick={() => void send(message.suggest![0])} disabled={pending}>
                              Try again
                            </button>
                          )}
                          {index === latestAssistantIndex && message.link && !message.failed && (
                            message.link[1].startsWith('#') ? (
                              <a className="aac-action primary" href={message.link[1]} onClick={() => handleLink(message.link![1])}>
                                {message.link[0]}
                              </a>
                            ) : message.link[1].startsWith('/') ? (
                              <Link
                                className="aac-action primary"
                                to={message.link[1]}
                                onClick={() => handleLink(message.link![1])}
                              >
                                {message.link[0]}
                              </Link>
                            ) : null
                          )}
                          {index === latestAssistantIndex && visibleSuggestions(message).length > 0 && !message.failed && !message.pending && (
                            <div className="aac-actions">
                              {visibleSuggestions(message).map((suggestion) => (
                                <button
                                  key={suggestion}
                                  className="aac-action"
                                  type="button"
                                  onClick={() => sendChip(suggestion, message.signup)}
                                  disabled={pending}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </article>
                ))}
              </>
            )}
          </div>
          <section className="aac-history" id="chat-history" aria-labelledby="history-title" hidden={!showHistory}>
            <div className="aac-history-heading">
              <h3 id="history-title" tabIndex={-1} ref={historyTitleRef}>Your conversations</h3>
              <button className="aac-icon" type="button" aria-label="Back to conversation" onClick={() => setShowHistory(false)}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </div>
            <p>Saved in this browser tab until you close it.</p>
            <div id="history-list">
              {[...chats].reverse().map((chat) => {
                const preview = chat.messages.find((message) => message.role === 'user')?.text || 'New conversation';
                return (
                  <button
                    key={chat.id}
                    className="aac-history-item"
                    type="button"
                    aria-current={chat.id === active}
                    onClick={() => {
                      setActive(chat.id);
                      setShowHistory(false);
                      inputRef.current?.focus();
                    }}
                  >
                    <strong>{preview}</strong>
                    <span>{new Date(chat.created).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
        <footer className="aac-footer">
          <form
            className={`aac-compose${voice.listening ? ' is-listening' : ''}`}
            onSubmit={(event) => {
              event.preventDefault();
              void send(draft, latestSignup?.wanted ? { wanted: true } : undefined);
            }}
          >
            <textarea
              id="prompt"
              ref={inputRef}
              rows={1}
              maxLength={800}
              aria-label="Message AACore AI"
              placeholder={
                voice.listening
                  ? 'Listening…'
                  : latestSignup?.step === 'password'
                    ? 'Use the password fields above…'
                    : 'Ask about AACore Booking...'
              }
              value={draft}
              disabled={pending}
              onChange={(event) => {
                listenBaseRef.current = event.target.value;
                setDraft(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  void send(draft, latestSignup?.wanted ? { wanted: true } : undefined);
                }
              }}
            />
            {voice.canListen ? (
              <button
                className={`aac-mic${voice.listening ? ' is-listening' : ''}`}
                type="button"
                aria-label={voice.listening ? 'Stop voice input' : 'Speak your message'}
                aria-pressed={voice.listening}
                title={voice.listening ? 'Stop listening' : 'Talk to Ask AACore AI'}
                disabled={pending}
                onClick={() => {
                  if (voice.listening) {
                    voice.stopListening();
                    return;
                  }
                  listenBaseRef.current = draft;
                  voice.startListening();
                }}
              >
                <Mic />
              </button>
            ) : null}
            <button className="aac-send" type="submit" aria-label="Send message" disabled={pending || !draft.trim()}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6" /></svg>
            </button>
          </form>
          <p className="aac-save" role="status">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10h.01" /></svg>
            <span>
              {voice.speechError
                || (latestSignup?.wanted
                  ? 'Passwords stay in private fields and are not kept in chat history.'
                  : 'Answers from AACore AI. I can also help create a customer account.')}
            </span>
          </p>
        </footer>
      </section>
      <button
        className="aac-launcher"
        id="launcher"
        type="button"
        aria-controls="aac-chat"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3ZM20 2v4m-2-2h4" /></svg>
        <span>Ask AACore <small>AI</small></span>
      </button>
    </>
  );
};

function SignupFields({
  signup,
  secrets,
  disabled,
  onSubmit,
}: {
  signup: AskAiSignupStatus;
  secrets?: SignupSecrets;
  disabled?: boolean;
  onSubmit: (query: string, extras: AskAiSignupDraft) => void;
}) {
  const [firstName, setFirstName] = useState(signup.firstName || '');
  const [lastName, setLastName] = useState(signup.lastName || '');
  const [email, setEmail] = useState(signup.email || '');
  const [phone, setPhone] = useState(signup.phone || '');
  const [firstNameError, setFirstNameError] = useState(signup.firstNameError || '');
  const [lastNameError, setLastNameError] = useState(signup.lastNameError || '');
  const [phoneError, setPhoneError] = useState(signup.phoneError || '');
  const [emailError, setEmailError] = useState(signup.emailError || '');
  const [password, setPassword] = useState(secrets?.password || '');
  const [confirmPassword, setConfirmPassword] = useState(secrets?.confirmPassword || '');
  const [showPassword, setShowPassword] = useState(false);
  const [birthMonth, setBirthMonth] = useState(signup.birthMonth ? String(signup.birthMonth) : '');
  const [birthDay, setBirthDay] = useState(signup.birthDay ? String(signup.birthDay) : '');
  const [agreed, setAgreed] = useState(Boolean(signup.acceptedAgreement));

  useEffect(() => {
    setFirstNameError(signup.firstNameError || '');
    setLastNameError(signup.lastNameError || '');
    setPhoneError(signup.phoneError || '');
    setEmailError(signup.emailError || '');
  }, [signup.firstNameError, signup.lastNameError, signup.phoneError, signup.emailError]);

  if (signup.step === 'name') {
    return (
      <form
        className="aac-signup"
        onSubmit={(event) => {
          event.preventDefault();
          const first = firstName.trim();
          const last = lastName.trim();
          if (first.length < 2 || last.length < 2) return;
          const nextFirstError = nameLooksFabricated(first) ? REGISTRATION_IDENTITY_MESSAGES.firstName : '';
          const nextLastError = nameLooksFabricated(last) ? REGISTRATION_IDENTITY_MESSAGES.lastName : '';
          setFirstNameError(nextFirstError);
          setLastNameError(nextLastError);
          if (nextFirstError || nextLastError) return;
          onSubmit(`My name is ${first} ${last}`, { wanted: true, firstName: first, lastName: last });
        }}
      >
        <label>
          First name
          <input
            className={firstNameError ? 'aac-invalid' : undefined}
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
              if (firstNameError) setFirstNameError('');
            }}
            autoComplete="given-name"
            disabled={disabled}
          />
        </label>
        {firstNameError ? <p className="aac-signup-error">{firstNameError}</p> : null}
        <label>
          Last name
          <input
            className={lastNameError ? 'aac-invalid' : undefined}
            value={lastName}
            onChange={(event) => {
              setLastName(event.target.value);
              if (lastNameError) setLastNameError('');
            }}
            autoComplete="family-name"
            disabled={disabled}
          />
        </label>
        {lastNameError ? <p className="aac-signup-error">{lastNameError}</p> : null}
        <button className="aac-action primary" type="submit" disabled={disabled || firstName.trim().length < 2 || lastName.trim().length < 2}>
          Continue
        </button>
      </form>
    );
  }

  if (signup.step === 'email') {
    return (
      <form
        className="aac-signup"
        onSubmit={(event) => {
          event.preventDefault();
          const value = email.trim().toLowerCase();
          if (!value.includes('@')) return;
          onSubmit(value, { wanted: true, email: value });
        }}
      >
        <label>
          Email
          <input
            type="email"
            className={emailError ? 'aac-invalid' : undefined}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (emailError) setEmailError('');
            }}
            autoComplete="email"
            disabled={disabled}
          />
        </label>
        {emailError ? <p className="aac-signup-error">{emailError}</p> : null}
        <button className="aac-action primary" type="submit" disabled={disabled || !email.includes('@')}>
          Continue
        </button>
      </form>
    );
  }

  if (signup.step === 'phone') {
    return (
      <form
        className="aac-signup"
        onSubmit={(event) => {
          event.preventDefault();
          const value = formatPhoneNumber(phone);
          if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(value)) return;
          const nextPhoneError = phoneLooksFabricated(value) ? REGISTRATION_IDENTITY_MESSAGES.phone : '';
          setPhoneError(nextPhoneError);
          if (nextPhoneError) return;
          onSubmit(value, { wanted: true, phone: value });
        }}
      >
        <label>
          Phone number
          <input
            type="tel"
            className={phoneError ? 'aac-invalid' : undefined}
            value={phone}
            onChange={(event) => {
              setPhone(formatPhoneNumber(event.target.value));
              if (phoneError) setPhoneError('');
            }}
            placeholder="(555) 123-4567"
            autoComplete="tel"
            disabled={disabled}
          />
        </label>
        {phoneError ? <p className="aac-signup-error">{phoneError}</p> : null}
        <button className="aac-action primary" type="submit" disabled={disabled || !/^\(\d{3}\) \d{3}-\d{4}$/.test(phone)}>
          Continue
        </button>
      </form>
    );
  }

  if (signup.step === 'password') {
    return (
      <form
        className="aac-signup"
        onSubmit={(event) => {
          event.preventDefault();
          if (!password || password !== confirmPassword) return;
          onSubmit("I've set a password.", { wanted: true, password, confirmPassword });
        }}
      >
        <label>
          Password
          <span className="aac-password-row">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              disabled={disabled}
            />
            <button type="button" className="aac-icon" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)}>
              {showPassword ? <EyeOff /> : <Eye />}
            </button>
          </span>
        </label>
        <label>
          Confirm password
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            disabled={disabled}
          />
        </label>
        {signup.passwordError ? <p className="aac-signup-error">{signup.passwordError}</p> : (
          <p className="aac-signup-hint">At least 8 characters, with uppercase, lowercase, and a number.</p>
        )}
        <button className="aac-action primary" type="submit" disabled={disabled || !password || password !== confirmPassword}>
          Continue
        </button>
      </form>
    );
  }

  if (signup.step === 'birthday') {
    return (
      <form
        className="aac-signup"
        onSubmit={(event) => {
          event.preventDefault();
          const month = parseInt(birthMonth, 10);
          const day = parseInt(birthDay, 10);
          if (!month || !day) return;
          onSubmit(`${MONTHS[month - 1]} ${day}`, { wanted: true, birthMonth: month, birthDay: day });
        }}
      >
        <div className="aac-signup-row">
          <label>
            Birth month
            <select value={birthMonth} onChange={(event) => setBirthMonth(event.target.value)} disabled={disabled}>
              <option value="">Select month</option>
              {MONTHS.map((name, index) => (
                <option key={name} value={index + 1}>{name}</option>
              ))}
            </select>
          </label>
          <label>
            Birth day
            <select value={birthDay} onChange={(event) => setBirthDay(event.target.value)} disabled={disabled}>
              <option value="">Select day</option>
              {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="aac-actions">
          <button className="aac-action primary" type="submit" disabled={disabled || !birthMonth || !birthDay}>
            Continue
          </button>
          <button className="aac-action" type="button" disabled={disabled} onClick={() => onSubmit('Skip', { wanted: true, skipBirthday: true })}>
            Skip
          </button>
        </div>
      </form>
    );
  }

  if (signup.step === 'agreement') {
    return (
      <form
        className="aac-signup"
        onSubmit={(event) => {
          event.preventDefault();
          setAgreed(true);
          onSubmit('I agree', { wanted: true, acceptedAgreement: true });
        }}
      >
        <label className="aac-signup-check">
          <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} disabled={disabled} />
          <span>
            I agree to the{' '}
            <a href={signup.agreementUrl || '/agreements/customer'} target="_blank" rel="noopener noreferrer">
              AACore Customer User Agreement (Terms of Service)
            </a>
          </span>
        </label>
        <button className="aac-action primary" type="submit" disabled={disabled}>
          I agree
        </button>
      </form>
    );
  }

  if (signup.step === 'confirm' || signup.ready) {
    return (
      <div className="aac-signup">
        <ul className="aac-signup-review">
          <li><strong>Name</strong> {[signup.firstName, signup.lastName].filter(Boolean).join(' ')}</li>
          <li><strong>Email</strong> {signup.email}</li>
          <li><strong>Phone</strong> {signup.phone}</li>
          <li>
            <strong>Birthday</strong>{' '}
            {signup.skipBirthday || !(signup.birthMonth && signup.birthDay)
              ? 'Skipped'
              : `${MONTHS[(signup.birthMonth || 1) - 1]} ${signup.birthDay}`}
          </li>
          <li><strong>Agreement</strong> Accepted</li>
        </ul>
        <button
          className="aac-action primary"
          type="button"
          disabled={disabled}
          onClick={() => onSubmit('Create my account', { wanted: true, confirm: true })}
        >
          Create my account
        </button>
      </div>
    );
  }

  return null;
}

export default PlatformAskAi;
